import {
  AmbientLight,
  AxesHelper,
  BufferAttribute,
  BufferGeometry,
  Color,
  GridHelper,
  HemisphereLight,
  LineBasicMaterial,
  LineLoop,
  Scene,
} from "three";
import { wuToMetres } from "../data/coords";
import type { Scene as SceneJson } from "../data/types";

/**
 * Build the static scaffolding of the scene: lighting, axes, ground grid,
 * and the court polygon outline. Returns the three.js scene so callers can
 * add their own dynamic objects (cameras, skeletons) on top.
 */
export function buildScene(sceneJson: SceneJson): Scene {
  const scene = new Scene();
  scene.background = new Color(0x111317);

  scene.add(new HemisphereLight(0xffffff, 0x222233, 0.9));
  scene.add(new AmbientLight(0xffffff, 0.15));

  // Origin axes: 2 m long, red=X, green=Y, blue=Z.
  scene.add(new AxesHelper(2));

  // Reference grid: 50 m × 50 m, 1 m cells. Lives at y=0.
  const grid = new GridHelper(50, 50, 0x444444, 0x222222);
  scene.add(grid);

  // Court polygon is (X, Z) at constant Y in the SOURCE frame. After the
  // up-rotation, a constant-Y source plane is no longer horizontal, so we
  // use the rotated XZ coords only for the outline shape and force every
  // vertex to the authoritative floor height (derived from median ankle Y
  // by the exporter; falls back to a wuToMetres of court.y).
  const poly = sceneJson.court.polygon;
  const floorYwu = sceneJson.court.y;
  let floorY: number;
  if (sceneJson.court.floor_y_metres != null) {
    floorY = sceneJson.court.floor_y_metres;
  } else {
    floorY = wuToMetres([0, floorYwu, 0])[1];
  }
  const positions = new Float32Array(poly.length * 3);
  for (let i = 0; i < poly.length; i++) {
    const [mx, , mz] = wuToMetres([poly[i][0], floorYwu, poly[i][1]]);
    positions[i * 3]     = mx;
    positions[i * 3 + 1] = floorY;
    positions[i * 3 + 2] = mz;
  }
  const geom = new BufferGeometry();
  geom.setAttribute("position", new BufferAttribute(positions, 3));
  const courtMat = new LineBasicMaterial({ color: 0xffaa33 });
  scene.add(new LineLoop(geom, courtMat));

  return scene;
}
